import { useEffect, useState } from 'react';
import { ConnectAccountOnboarding } from '@stripe/react-connect-js';
import {
  AlertIcon,
  CheckIcon,
  ClockIcon,
  Logo,
  Note,
  Panel,
  RefreshIcon,
  ShieldIcon,
} from '@wyocspay/ui';
import { ApiError, api, type LogoState, type Overview } from './api.js';
import { BrandLogo } from './BrandLogo.js';
import { ConnectGate } from './connect.js';
import { describeRequirements, groupRequirements } from './requirements.js';
import { accountNeed } from './account-state.js';
import { TicketsView } from './Tickets.js';

/**
 * Stripe Embedded Onboarding, as a screen rather than a panel.
 *
 * Spec: "Use Stripe Embedded Onboarding. WyoCSPay must not manually collect or
 * validate merchant identity documents, director passports, beneficial-owner
 * identity documents or bank verification documents."
 *
 * Everything that matters renders inside Stripe's own component, sharing the
 * panel-wide Connect instance. Documents go from the merchant to Stripe; this
 * application has no field, endpoint or table for them.
 *
 * This used to be one view among twenty in a dashboard whose every other panel
 * was empty, because an account that cannot transact has no payments, no
 * balance and no payouts to show. A merchant signed in and had to work out for
 * themselves which of the errors on screen was the one that mattered. Now the
 * screen stands alone in front of the panel until Stripe says the account can
 * take money - see the gate in App.tsx - and its job is to answer three
 * questions without being asked: what is happening, who is asking, and what is
 * left.
 */

/** What we know about the account, from the server. */
type Account = Overview['merchant'];

// ---------------------------------------------------------------------------

/**
 * "Add your logo", asked once while a merchant is setting the account up.
 *
 * Onboarding is the only moment a merchant is deliberately going through the
 * things that make their account theirs, so it is the right moment to ask, and
 * the wrong moment to insist. The panel appears if there is no logo and nobody
 * has asked yet; uploading one or answering "not now" records that the question
 * has been put, on the account rather than in this browser, and it is not asked
 * again. Settings keeps the same control permanently for anybody who changes
 * their mind.
 *
 * It renders outside the Stripe component below on purpose: this is a WyoCSPay
 * upload, not part of the verification Stripe is carrying out, and a merchant
 * must never be left thinking a logo is something their regulator asked for.
 */
function LogoPrompt() {
  const [state, setState] = useState<LogoState | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .logo()
      .then((s) => {
        if (!cancelled) setState(s);
      })
      .catch(() => {
        // Nothing to say. A prompt about an optional logo is not worth an error
        // message on the screen where somebody is being verified.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!state || dismissed || state.present || state.prompted) return null;

  return (
    <Panel title="Add your logo (optional)">
      <p style={{ marginTop: 0, fontSize: 13.5, color: 'var(--wc-muted)', lineHeight: 1.55 }}>
        Your customers will pay on <strong>pay.wyocs.org</strong>, which carries our name rather than
        yours. Your business name appears on every screen of that flow already; a logo beside it is
        what makes a customer certain at a glance that they are in the right place. You can add it
        later, or never - with no logo the page simply shows your name on its own.
      </p>
      <BrandLogo compact initial={state} onSkip={() => setDismissed(true)} />
    </Panel>
  );
}

// ---------------------------------------------------------------------------

/**
 * Where the account stands, stated as facts rather than as a percentage.
 *
 * There is no honest percentage to show. The number of things Stripe asks for
 * changes as it learns about the business - answering "company" adds directors
 * and owners that answering "individual" would not - so a bar that reached 80%
 * and then fell back to 50% would be a lie told twice. These four are each
 * either true or not, they come from the server, and the merchant can check
 * every one of them against what they have actually done.
 */
function Progress({ account }: { account: Account }) {
  const outstanding = account.requirements_due.length;

  const steps: Array<{ label: string; done: boolean; pending?: string }> = [
    {
      label: 'Your details are with Stripe',
      done: account.details_submitted,
      pending: 'Fill in the form above and submit it.',
    },
    {
      label: 'Nothing outstanding',
      done: outstanding === 0,
      pending:
        outstanding === 1 ? 'One thing is still needed.' : `${outstanding} things are still needed.`,
    },
    {
      label: 'You can take payments',
      done: account.charges_enabled,
      pending: 'Stripe enables this once it has finished checking.',
    },
    {
      label: 'You can be paid out to your bank',
      done: account.payouts_enabled,
      pending: 'Enabled separately from payments, and sometimes a little later.',
    },
  ];

  return (
    <Panel title="Where you are">
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 12 }}>
        {steps.map((step) => (
          <li key={step.label} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <span
              aria-hidden
              style={{
                flex: 'none',
                marginTop: 2,
                color: step.done ? 'var(--wc-green)' : 'var(--wc-amber)',
                display: 'inline-flex',
              }}
            >
              {step.done ? <CheckIcon size={15} /> : <ClockIcon size={15} />}
            </span>
            <span style={{ minWidth: 0 }}>
              <span style={{ fontSize: 14, fontWeight: step.done ? 400 : 600 }}>
                {step.label}
                <span className="wc-sr-only">{step.done ? ' - done' : ' - not yet'}</span>
              </span>
              {!step.done && step.pending ? (
                <span
                  style={{
                    display: 'block',
                    fontSize: 12.5,
                    color: 'var(--wc-faint)',
                    marginTop: 2,
                  }}
                >
                  {step.pending}
                </span>
              ) : null}
            </span>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

// ---------------------------------------------------------------------------

/**
 * Stripe's outstanding list, in words.
 *
 * The translation and the grouping both live in requirements.ts; this only lays
 * them out. The raw Stripe keys ride along in the `title` attribute so that a
 * merchant on the phone to support can read out something an operator can search
 * for, without the screen itself being written in machine.
 */
function Outstanding({ account }: { account: Account }) {
  const items = describeRequirements(account.requirements_due);

  if (items.length === 0) {
    return (
      <Panel title="What Stripe is waiting for">
        <p style={{ margin: 0, fontSize: 14, color: 'var(--wc-muted)', lineHeight: 1.6 }}>
          {account.details_submitted
            ? 'Nothing from you. Stripe has everything it asked for and is checking it. This is ' +
              'usually minutes, occasionally a day or two if something needs a human. You do not ' +
              'have to wait on this page - we will have the account open when you next sign in.'
            : 'Stripe has not asked for anything yet. Start the form above and it will tell you ' +
              'what it needs as it goes.'}
        </p>
      </Panel>
    );
  }

  return (
    <Panel
      title={
        items.length === 1
          ? 'What Stripe is waiting for - 1 item'
          : `What Stripe is waiting for - ${items.length} items`
      }
    >
      <p style={{ marginTop: 0, fontSize: 13.5, color: 'var(--wc-muted)', lineHeight: 1.55 }}>
        You fill all of this in inside Stripe's form above. It is listed here so you know what to
        have to hand before you start.
      </p>

      <div style={{ display: 'grid', gap: 18 }}>
        {groupRequirements(items).map((section) => (
          <div key={section.group}>
            <h3
              style={{
                margin: '0 0 8px',
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.09em',
                textTransform: 'uppercase',
                color: 'var(--wc-faint)',
              }}
            >
              {section.title}
            </h3>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 10 }}>
              {section.items.map((item) => (
                <li
                  key={item.key}
                  title={item.raw.join(', ')}
                  style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}
                >
                  <span
                    aria-hidden
                    style={{ flex: 'none', marginTop: 2, color: 'var(--wc-amber)', display: 'inline-flex' }}
                  >
                    <AlertIcon size={14} />
                  </span>
                  <span style={{ minWidth: 0 }}>
                    <span style={{ fontSize: 14 }}>{item.label}</span>
                    {item.detail ? (
                      <span
                        style={{
                          display: 'block',
                          fontSize: 12.5,
                          color: 'var(--wc-faint)',
                          lineHeight: 1.55,
                          marginTop: 2,
                        }}
                      >
                        {item.detail}
                      </span>
                    ) : null}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </Panel>
  );
}

// ---------------------------------------------------------------------------

/**
 * The body of the screen, and also the Onboarding view inside the panel once
 * the gate has lifted.
 *
 * One component for both because they are the same thing. Requirements can come
 * back later - Stripe asks for a renewed document, or for details it did not
 * need at first - and when that happens without charges being switched off, the
 * merchant needs this page inside a panel they can still use.
 *
 * `onRecheck(askStripe)` re-reads the account from our server. `askStripe` makes
 * the server read Stripe directly first, which is what the exit callback and the
 * button below want; without it the server answers from what the webhook last
 * told it, which is cheap enough to poll.
 */
export function Onboarding({
  account,
  onRecheck,
  openForm = false,
}: {
  account: Account;
  onRecheck: (askStripe: boolean) => Promise<void>;
  /**
   * Open Stripe's form straight away on a verified account.
   *
   * Set when the merchant arrived from somewhere that knows there is something
   * to provide - a payment method Stripe is holding pending a document. The
   * account itself can be clean while a single capability is not, and in that
   * case the settled panel below is the wrong answer on its own: it would tell
   * somebody sent here to hand something over that there is nothing to do.
   */
  openForm?: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ tone: 'ok' | 'warn' | 'danger'; text: string } | null>(null);
  const [showForm, setShowForm] = useState(openForm);

  async function check(): Promise<void> {
    setBusy(true);
    setResult(null);
    try {
      const r = await api.connectRefresh();
      if (r.charges_enabled) {
        setResult({
          tone: 'ok',
          text:
            r.unlocked.length > 0
              ? `Stripe has verified your account. Unlocked: ${r.unlocked.join(', ')}.`
              : 'Stripe has verified your account. You can accept payments.',
        });
      } else {
        setResult({
          tone: 'warn',
          text:
            r.requirements_due.length > 0
              ? 'Stripe still needs a few things. The list below has been brought up to date.'
              : 'Stripe has not enabled charges yet. Verification can take a few minutes.',
        });
      }
      // Whatever Stripe said, the panel now reads it from our own record rather
      // than from the reply above, so one answer is on screen and not two.
      await onRecheck(false);
    } catch (err) {
      setResult({ tone: 'danger', text: err instanceof ApiError ? err.message : String(err) });
    } finally {
      setBusy(false);
    }
  }

  const suspended = account.status === 'SUSPENDED' || account.status === 'CLOSED';

  if (suspended) {
    return (
      <Note tone="danger" title="This account is not open">
        Your WyoCSPay account is {account.status === 'CLOSED' ? 'closed' : 'suspended'}, so there is
        no point starting verification - it would not enable anything. Open a support ticket below
        and we will tell you why and what can be done about it.
      </Note>
    );
  }

  /**
   * Verified, with nothing outstanding: say so once and stop talking.
   *
   * This page used to render the full apparatus regardless - Stripe's form, a
   * checklist of four ticks that were all green, an empty "what Stripe is
   * waiting for", a logo prompt and a panel headed "finished, and still not
   * open?" - to merchants who were finished and open. Read literally it told
   * them their verified account still needed verifying, and it was the loudest
   * thing in the panel while being the least true.
   *
   * What remains is the one fact, the one thing worth knowing beside it, and the
   * route to changing details. The form is not here because there is nothing to
   * submit; the checklist is not here because a list whose every entry is "yes"
   * is a list nobody needs to read.
   */
  if (accountNeed(account) === null) {
    return (
      <div style={{ display: 'grid', gap: 20 }}>
        <Note tone="ok" title="Your account is verified">
          Stripe has checked your business and enabled charges. There is nothing outstanding and
          nothing for you to do here.
          {account.payouts_enabled ? null : (
            /**
             * Charges on, payouts not yet. Not a task, so not a banner.
             *
             * Stripe enables the two separately and the second sometimes lands a
             * little later on its own. There is no action to offer, which is
             * exactly why this belongs on the page somebody opened deliberately
             * rather than on the overview everybody sees.
             */
            <div style={{ marginTop: 10, fontSize: 13.5 }}>
              Payouts to your bank are not switched on yet. Stripe enables that separately from
              payments and it often follows within a day, without needing anything from you.
            </div>
          )}
        </Note>

        {/*
          The form, on a verified account, for the cases the account state
          cannot see.
        *
          A capability can be held pending something - a document for a bank
          debit, a detail for an instalment provider - while the account itself
          shows nothing due, because those requirements hang off the capability
          rather than the account. Somebody who arrives from Payment methods to
          hand that over must land on a form, not on a page congratulating them.
        *
          It stays inside this page, as everywhere else: the merchant never gets
          sent to a Stripe URL, and WyoCSPay never sees what they upload.
        */}
        {showForm ? (
          <ConnectGate what="Stripe verification">
            <section
              className="wc-panel"
              style={{ padding: 'clamp(16px, 3vw, 30px)', minHeight: 320 }}
              aria-label="Stripe verification"
            >
              <ConnectAccountOnboarding
                onExit={() => {
                  void onRecheck(true);
                }}
                onStepChange={() => {
                  void onRecheck(false);
                }}
              />
            </section>
          </ConnectGate>
        ) : null}

        <Panel title="Changing your business details, or sending Stripe a document">
          <p style={{ marginTop: 0, fontSize: 13.5, color: 'var(--wc-muted)', lineHeight: 1.55 }}>
            Your address, bank account, representative and documents are held by Stripe, not by us.
            Open Stripe's form here to change any of them, or to provide something Stripe has asked
            for against a single payment method - that can happen while the account itself shows
            nothing outstanding. It runs inside this page and what you upload goes straight to
            Stripe.
          </p>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {showForm ? null : (
              <button type="button" className="wc-btn" onClick={() => setShowForm(true)}>
                Open Stripe's form
              </button>
            )}
            <button
              type="button"
              className={showForm ? 'wc-btn' : 'wc-btn wc-btn-secondary'}
              onClick={check}
              disabled={busy}
            >
              <RefreshIcon size={14} /> {busy ? 'Checking…' : 'Check with Stripe again'}
            </button>
          </div>
          {result ? (
            <div style={{ marginTop: 12 }}>
              <Note tone={result.tone}>{result.text}</Note>
            </div>
          ) : null}
        </Panel>
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <Note title="Stripe verifies you. We never see the documents.">
        Every business taking card payments has to be identified before it can, and Stripe does that
        checking for us. The form below is Stripe's own, running inside this page: what you type and
        anything you upload - passports, licences, bank details - goes from your browser straight to
        Stripe. WyoCSPay is told one thing at the end of it, which is whether your account may take
        payments. We have no screen, no database column and no way of retrieving any of the rest.
      </Note>

      {/*
        The form, given the room it deserves.

        This is the screen, not a card on it. It is deliberately the widest and
        least decorated thing here, with nothing competing for attention above it
        beyond the one paragraph that says whose form it is. The status and the
        checklist go below, where they are read after the work rather than
        instead of it.
      */}
      <ConnectGate what="Stripe verification">
        <section
          className="wc-panel"
          style={{ padding: 'clamp(16px, 3vw, 30px)', minHeight: 320 }}
          aria-label="Stripe verification"
        >
          <ConnectAccountOnboarding
            /**
             * Stripe fires this when the merchant leaves its flow, having
             * finished or having stopped part way. Either way the right move is
             * to ask our own server what Stripe now says, rather than to assume
             * that reaching the end means verified - it frequently does not, and
             * a panel that unlocked itself on an assumption would show a
             * dashboard whose every request then failed.
             */
            onExit={() => {
              void onRecheck(true);
            }}
            /**
             * Refresh on every step as well, quietly and without asking Stripe.
             *
             * The checklist beside the form is only useful if it keeps up with
             * it. This reads our own record, which the `account.updated` webhook
             * keeps current, so it costs one small query and never a Stripe call.
             */
            onStepChange={() => {
              void onRecheck(false);
            }}
          />
        </section>
      </ConnectGate>

      {result ? <Note tone={result.tone}>{result.text}</Note> : null}

      <Progress account={account} />
      <Outstanding account={account} />

      <LogoPrompt />

      <Panel title="Finished with Stripe, and still not open?">
        <p style={{ marginTop: 0, fontSize: 13.5, color: 'var(--wc-muted)', lineHeight: 1.55 }}>
          This page keeps itself up to date on its own - Stripe tells us the moment it verifies you,
          and there is nothing you need to press. If you have finished and it still says otherwise
          after a few minutes, this reads your status straight from Stripe rather than from our copy
          of it.
        </p>
        <button type="button" className="wc-btn" onClick={check} disabled={busy}>
          <RefreshIcon size={14} /> {busy ? 'Checking with Stripe…' : 'Check my status with Stripe'}
        </button>
      </Panel>
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * The whole screen, for a merchant who has nothing else to see.
 *
 * Its own chrome rather than the panel's: there is no navigation because there
 * is nowhere to navigate to, and a sidebar of sections that all fail would be an
 * invitation to go and find out how. What it does keep is the header - the
 * logo, who is signed in, and a way out - because a screen you cannot sign out
 * of is a trap, and because a merchant who has landed here needs to recognise
 * where "here" is.
 */
export function OnboardingScreen({
  account,
  user,
  onLogout,
  onRecheck,
}: {
  account: Account;
  user: string;
  onLogout: () => void;
  onRecheck: (askStripe: boolean) => Promise<void>;
}) {
  const [helping, setHelping] = useState(false);

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <header
        style={{
          background: 'var(--wc-blue-header)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          padding: '13px 20px',
          flexWrap: 'wrap',
        }}
      >
        <Logo height={56} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, fontSize: 13 }}>
          <span style={{ color: '#dbe9f2' }}>{user}</span>
          <button
            type="button"
            onClick={() => setHelping((h) => !h)}
            className="wc-btn wc-btn-sm"
            style={{ background: 'transparent', borderColor: 'rgba(255,255,255,0.35)' }}
          >
            {helping ? 'Back to verification' : 'Get help'}
          </button>
          <button
            type="button"
            onClick={onLogout}
            className="wc-btn wc-btn-sm"
            style={{ background: 'transparent', borderColor: 'rgba(255,255,255,0.35)' }}
          >
            Sign out
          </button>
        </div>
      </header>

      <main style={{ flex: 1, padding: '26px 20px 56px' }}>
        <div style={{ maxWidth: 880, margin: '0 auto' }}>
          <div style={{ marginBottom: 20 }}>
            <h1 style={{ fontSize: 24, margin: 0 }}>
              {helping ? 'Support' : 'Let us get you verified'}
            </h1>
            <p style={{ margin: '5px 0 0', color: 'var(--wc-muted)', fontSize: 14.5, lineHeight: 1.55 }}>
              {helping
                ? 'Ask us anything, attach what you have, and keep the thread. Your verification is exactly where you left it.'
                : 'Your account is open, but it cannot take payments until Stripe has checked who you are. That is the only thing standing between you and the rest of the panel.'}
            </p>
          </div>

          {/*
            Both are always mounted, and one is hidden.

            Unmounting the Stripe component to show a support thread would throw
            away whatever the merchant had half-typed into it, and the moment
            somebody is most likely to want help is the moment they are stuck
            part way through a form. `hidden` keeps the iframe alive and its
            state with it. It costs one offscreen iframe.
          */}
          <div hidden={helping}>
            <Onboarding account={account} onRecheck={onRecheck} />
          </div>

          <div hidden={!helping}>
            <TicketsView />
          </div>

          {!helping ? (
            <p
              style={{
                margin: '24px 0 0',
                fontSize: 13,
                color: 'var(--wc-faint)',
                display: 'flex',
                gap: 8,
                alignItems: 'flex-start',
                lineHeight: 1.6,
              }}
            >
              <span aria-hidden style={{ flex: 'none', marginTop: 2, display: 'inline-flex' }}>
                <ShieldIcon size={14} />
              </span>
              <span>
                Stuck on something, or Stripe asking for a document you do not think applies to you?{' '}
                <button
                  type="button"
                  onClick={() => setHelping(true)}
                  className="wc-btn wc-btn-sm wc-btn-secondary"
                  style={{ verticalAlign: 'baseline' }}
                >
                  Open a support ticket
                </button>{' '}
                and a person will read it.
              </span>
            </p>
          ) : null}
        </div>
      </main>
    </div>
  );
}
